import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import {
    TitleComponent,
    TooltipComponent,
    LegendComponent
} from 'echarts/components';
import { PieChart } from 'echarts/charts';
import { LabelLayout } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    PieChart,
    CanvasRenderer,
    LabelLayout
]);

const ProductSalesPieChart = ({ chartType, sale_summary = [] }) => {
    const chartRef = useRef(null);

    useEffect(() => {
        if (!sale_summary.length) return;

        const chartDom = chartRef.current;
        const myChart = echarts.init(chartDom);

        // Process the sale_summary data
        const processedData = sale_summary.map(item => ({
            name: item.product_name,
            sales: parseInt(item.total_sale_qty) || 0
        }));

        // Sort and select top/bottom 5 based on chartType
        const sortedData = [...processedData].sort((a, b) =>
            chartType === 'DESC' ? b.sales - a.sales : a.sales - b.sales
        );
        const displayData = sortedData.slice(0, 5);
        const totalSales = displayData.reduce((sum, item) => sum + item.sales, 0);

        const option = {
            title: {
                text: chartType === 'DESC' ? 'Top 5 Selling Products' : 'Last 5 Selling Products',
                subtext: `Total: ${totalSales} units sold`,
                left: 'center',
                textStyle: {
                    fontSize: 16,
                    fontWeight: 'bold'
                }
            },
            tooltip: {
                trigger: 'item',
                formatter: function(params) {
                    return `
                        <strong>${params.name}</strong><br/>
                        Sales: ${params.value} units<br/>
                        Percentage: ${params.percent}%
                    `;
                }
            },
            legend: {
                orient: 'vertical',
                left: 'left',
                data: displayData.map(item => item.name),
                textStyle: {
                    overflow: 'truncate',
                    width: 150,
                    ellipsis: '...'
                }
            },
            series: [
                {
                    name: 'Sales Quantity',
                    type: 'pie',
                    radius: ['40%', '70%'],
                    avoidLabelOverlap: false,
                    itemStyle: {
                        borderRadius: 10,
                        borderColor: '#fff',
                        borderWidth: 2
                    },
                    label: {
                        show: true,
                        formatter: '{b}: {c}'
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: '18',
                            fontWeight: 'bold'
                        }
                    },
                    labelLine: {
                        show: true
                    },
                    data: displayData.map(item => ({
                        value: item.sales,
                        name: item.name
                    }))
                }
            ],
            color: [
                '#3b82f6', // blue-500
                '#ec4899', // pink-500
                '#10b981', // emerald-500
                '#f59e0b', // amber-500
                '#6366f1'  // indigo-500
            ]
        };

        myChart.setOption(option);

        const handleResize = () => {
            myChart.resize();
        };
        window.addEventListener('resize', handleResize);

        return () => {
            myChart.dispose();
            window.removeEventListener('resize', handleResize);
        };
    }, [chartType, sale_summary]);

    return (
        <div className="card shadow-sm">
            <div className="card-body p-3">
                {sale_summary.length > 0 ? (
                    <div ref={chartRef} style={{ width: '100%', height: '400px' }}></div>
                ) : (
                    <div className="text-center py-5">
                        <p className="text-muted">No sales data available</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductSalesPieChart;